import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import { marqueeCopies } from '@theme/eme-grid-core';

const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

class EmeGridMarquee extends Component {
  #controller = null;
  #width = 0;
  #holds = new Set();
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    mediaQueryLarge.addEventListener('change', () => this.#sync(), { signal });
    reducedMotionQuery.addEventListener('change', () => this.#sync(), { signal });
    this.#toggle?.addEventListener('click', () => this.#toggleUserPause(), { signal });
    window.addEventListener('resize', () => this.#handleResize(), { signal, passive: true });
    this.addEventListener('pointerdown', () => this.#hold('press'), { signal });
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      this.addEventListener(type, () => this.#release('press'), { signal });
    }
    this.addEventListener('pointerenter', (event) => this.#holdIfMouse(event), { signal });
    this.addEventListener('pointerleave', () => this.#release('hover'), { signal });
    this.addEventListener('focusin', (event) => this.#holdFocus(event), { signal });
    this.addEventListener('focusout', (event) => this.#releaseFocus(event), { signal });
    this.#width = window.innerWidth;
    this.#sync();
    this.#syncWhenFontsLoad(signal);
    this.#observeOriginals(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get #toggle() {
    return this.querySelector(':scope > [data-eme-marquee-toggle]');
  }

  get #track() {
    return this.querySelector(':scope > [data-eme-marquee-track]');
  }

  async #syncWhenFontsLoad(signal) {
    await document.fonts.ready;
    if (!signal.aborted) this.#sync();
  }

  #observeOriginals(signal) {
    const track = this.#track;
    if (!track) return;
    const observer = new MutationObserver((records) => {
      if (records.some((record) => this.#changesOriginals(record, track))) this.#scheduleSync();
    });
    observer.observe(track, { childList: true, characterData: true, subtree: true });
    signal.addEventListener('abort', () => {
      observer.disconnect();
      cancelAnimationFrame(this.#frame);
    });
  }

  #changesOriginals(record, track) {
    if (this.#isCopy(record.target)) return false;
    if (record.type !== 'childList' || record.target !== track) return true;
    return [...record.addedNodes, ...record.removedNodes].some((node) => !this.#isCopy(node));
  }

  #isCopy(node) {
    const element = node instanceof Element ? node : node.parentElement;
    return Boolean(element?.closest('[data-eme-marquee-copy]'));
  }

  #scheduleSync() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  }

  #handleResize() {
    if (window.innerWidth === this.#width) return;
    this.#width = window.innerWidth;
    this.#sync();
  }

  #sync() {
    const track = this.#track;
    if (!track) return;
    for (const copy of track.querySelectorAll(':scope > [data-eme-marquee-copy]')) copy.remove();
    track.style.removeProperty('--eme-grid-marquee-duration');
    if (mediaQueryLarge.matches || prefersReducedMotion()) return;

    const originals = [...track.children];
    const contentWidth = originals.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
    const copies = marqueeCopies(this.clientWidth, contentWidth);
    for (let round = 0; round < copies * 2 - 1; round += 1) {
      for (const item of originals) track.append(this.#copyOf(item));
    }
    track.style.setProperty('--eme-grid-marquee-duration', `${(Number(this.dataset.duration) || 22) * copies}s`);
  }

  #copyOf(item) {
    const copy = item.cloneNode(true);
    for (const element of [copy, ...copy.querySelectorAll('[id]')]) element.removeAttribute('id');
    for (const element of [copy, ...copy.querySelectorAll('[data-shopify-editor-block]')]) {
      element.removeAttribute('data-shopify-editor-block');
    }
    copy.setAttribute('data-eme-marquee-copy', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    return copy;
  }

  #holdIfMouse(event) {
    if (event.pointerType === 'mouse') this.#hold('hover');
  }

  #holdFocus(event) {
    if (event.target !== this.#toggle) this.#hold('focus');
  }

  #toggleUserPause() {
    const toggle = this.#toggle;
    if (!toggle) return;
    if (this.#holds.has('user')) this.#release('user');
    else this.#hold('user');
    toggle.textContent = this.#holds.has('user') ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
  }

  #releaseFocus(event) {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#release('focus');
  }

  #hold(reason) {
    this.#holds.add(reason);
    this.toggleAttribute('data-paused', true);
  }

  #release(reason) {
    this.#holds.delete(reason);
    this.toggleAttribute('data-paused', this.#holds.size > 0);
  }
}

if (!customElements.get('eme-grid-marquee')) customElements.define('eme-grid-marquee', EmeGridMarquee);
